import { Group, Stack, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import type { MatchSuggestion } from '../../types';

type MatchReasonExplainerProps = {
  suggestion: MatchSuggestion;
};

function findCore(
  feedRef: string,
  assetRef: string,
): { before: string; core: string; after: string } {
  const idx = feedRef.toLowerCase().indexOf(assetRef.toLowerCase());
  if (idx !== -1) {
    return {
      before: feedRef.slice(0, idx),
      core: feedRef.slice(idx, idx + assetRef.length),
      after: feedRef.slice(idx + assetRef.length),
    };
  }
  return { before: '', core: feedRef, after: '' };
}

function humaniseReason(detail: string, type: string): string {
  if (type === 'KNOWN_AFFIX') {
    return `References match after removing ${detail.replace('stripped ', '')}`;
  }
  if (type === 'SUBSTRING_CONTAINMENT') {
    return 'One reference number appears inside the other';
  }
  return detail;
}

const pill = { borderRadius: 3 } as const;

export default function MatchReasonExplainer({ suggestion }: MatchReasonExplainerProps) {
  const feedRef = suggestion.unprocessedProduct.providerReferenceNumber;
  const assetRef = suggestion.suggestedAsset.providerReferenceNumber;
  const { matchReasons } = suggestion;
  const { before, core, after } = findCore(feedRef, assetRef);

  return (
    <Stack gap="xs">
      <Group gap="sm" align="center">
        <Text size="sm">
          {before && (
            <Text component="span" size="sm" c="dimmed" bg="gray.1" px={4} style={pill}>
              {before}
            </Text>
          )}
          <Text component="span" size="sm" fw={600} bg="green.0" px={4} style={pill}>
            {core}
          </Text>
          {after && (
            <Text component="span" size="sm" c="dimmed" bg="gray.1" px={4} style={pill}>
              {after}
            </Text>
          )}
        </Text>
        <IconArrowRight size={14} color="var(--mantine-color-dimmed)" />
        <Text size="sm" fw={600} bg="green.0" px={4} style={pill}>
          {assetRef}
        </Text>
      </Group>
      <Stack gap={2}>
        {matchReasons.map((reason, i) => (
          <Text key={i} size="xs" c="dimmed">
            {humaniseReason(reason.detail, reason.type)}
          </Text>
        ))}
      </Stack>
    </Stack>
  );
}
